<template>
  <div v-if="isOrderActionEnabled">
    <b-button
      v-if="order.shouldAddProducts && isBrand"
      variant="info"
      class="font-weight-bolder mr-1"
      @click="$bvModal.show(addProductModalId)"
    >
      Add Products
    </b-button>
    <b-dropdown
      v-if="isModifyButtonEnabled && isModifyOrderImportExportEnabled && !isModify"
      text="Modify"
      variant="info"
      class="font-weight-bolder mr-1"
      split
      right
      toggle-class="modify-dropdown-toggle"
      @click="enableModify(false)"
    >
      <b-dropdown-item @click="$emit('export-order-for-modify')">
        Export Order
      </b-dropdown-item>
      <b-dropdown-item @click="$emit('open-import-modal')">
        Import Order
      </b-dropdown-item>
    </b-dropdown>
    <b-button
      v-if="isModifyButtonEnabled && (!isModifyOrderImportExportEnabled || isModify)"
      variant="info"
      class="font-weight-bolder mr-1"
      @click="enableModify(false)"
    >
      {{ isModify ? 'Revert changes' : 'Modify' }}
    </b-button>
    <b-dropdown
      :text="selectedAction.text"
      variant="none"
      class="order-action"
      split
      :disabled="isModify && hasError"
      right
      @click="openActionModal(selectedAction.action)"
    >
      <b-dropdown-item
        v-if="isApproveEnabled && !isModify"
        @click="openActionModal(ORDER_STATUS.CONFIRMED)"
      >
        {{ approveActionText }}
      </b-dropdown-item>
      <!-- KP-4363: confirm the products ticked in the products list -->
      <b-dropdown-item
        v-if="canConfirmSelectedProducts"
        :disabled="loading"
        @click="openConfirmSelectedModal"
      >
        Confirm selected products ({{ selectedProductIds.length }})
      </b-dropdown-item>
      <b-dropdown-item
        v-if="isModifyButtonEnabled && isModify"
        @click="openActionModal(SAVE)"
      >
        Save
      </b-dropdown-item>
      <b-dropdown-item
        v-if="isModifyButtonEnabled && isModify && isModifyProductsAllowed"
        @click="openActionModal(MODIFY)"
      >
        Modify Order
      </b-dropdown-item>
      <b-dropdown-item
        v-if="isModifyButtonEnabled && isModify && isModifyProductsAllowed"
        @click="openActionModal(MODIFY_AND_CONFIRM)"
      >
        Modify & Confirm Order
      </b-dropdown-item>
      <b-dropdown-item
        v-if="isCancelEnabled"
        @click="openActionModal(ORDER_STATUS.CANCELLED)"
      >
        Cancel
      </b-dropdown-item>
      <b-dropdown-item
        v-if="isRejectEnabled && !isModify"
        @click="openActionModal(ORDER_STATUS.REJECTED)"
      >
        Reject
      </b-dropdown-item>
    </b-dropdown>
    <b-modal
      id="modal-date"
      ref="expAvailability"
      hide-header
      hide-header-close
      hide-footer
      no-close-on-backdrop
      centered
    >
      <div class="pb-5-px border-bottom">
        <span
          class="custom-modal-heading color-dark-black"
        >Please select estimated delivery window</span>
        <feather-icon
          class="float-right close-x-icon"
          icon="XIcon"
          size="24"
          @click="$refs.expAvailability.hide()"
        />
      </div>
      <div class="mt-1">
        <div class="d-inline-flex align-items-center position-relative">
          <span class="color-dark-black font-weight-bold mr-1">
            Estimated delivery Window:
          </span>
          <flat-pickr
            ref="expDate"
            v-model="expectedDate"
            class="form-control mt-0 exp-av-date"
            :config="VUE_FLATPICKR_MONTH_CONFIG"
            :placeholder="expectedDate || KP_MONTH_FORMAT.PLACEHOLDER"
          />
          <feather-icon
            icon="CalendarIcon"
            size="20"
            class="position-absolute position-right-0 mr-6-px"
            @click="toggleDatePicker"
          />
        </div>
      </div>
      <div class="text-center my-1">
        <b-button
          type="submit"
          variant="info"
          class="mt-1 mr-2 px-sm-3 font-weight-bolder"
          :disabled="loading || !expectedDate"
          @click="onClickSaveDate"
        >
          {{ loading ? 'Saving' : 'Save' }}
        </b-button>
        <b-button
          type="reset"
          class="mt-1 px-sm-3 font-weight-bolder"
          variant="outline-info"
          @click="$refs.expAvailability.hide()"
        >
          Cancel
        </b-button>
      </div>
    </b-modal>
    <b-modal
      id="modal-approve"
      ref="actionModal"
      hide-header
      hide-header-close
      hide-footer
      modal-class="modal-approve"
      centered
    >
      <div class="pb-5-px border-bottom">
        <span class="custom-modal-heading color-dark-black">{{
          modalTitle
        }}</span>
        <feather-icon
          class="float-right close-x-icon"
          icon="XIcon"
          size="24"
          @click="$refs.actionModal.hide()"
        />
      </div>
      <div class="mt-1">
        <b-card-text class="color-dark-black font-weight-bold">
          {{ modalBody }}
        </b-card-text>
        <b-form-checkbox
          v-if="action === SAVE"
          v-model="sendEmailToRetailer"
          class="mt-1"
        >
          Send notification to retailer
        </b-form-checkbox>
      </div>
      <div class="text-center my-1">
        <b-button
          type="submit"
          variant="info"
          class="mt-1 mr-2 px-sm-3 font-weight-bolder"
          :disabled="loading"
          @click="confirmAction(action)"
        >
          {{ loading ? 'Confirming' : 'Confirm' }}
        </b-button>
        <b-button
          type="reset"
          class="mt-1 px-sm-3 font-weight-bolder"
          variant="outline-info"
          @click="$refs.actionModal.hide()"
        >
          Cancel
        </b-button>
      </div>
    </b-modal>
    <b-modal
      :id="addProductModalId"
      size="xl"
      hide-header
      hide-header-close
      hide-footer
      no-close-on-backdrop
      centered
    >
      <div class="py-1 border-bottom">
        <span
          class="custom-modal-heading color-dark-black"
        >Add Products Window</span>
        <feather-icon
          class="float-right close-x-icon"
          icon="XIcon"
          size="24"
          @click="$bvModal.hide(addProductModalId)"
        />
      </div>
      <div>
        <add-order-products
          :collection-id="order.collectionId"
          :order="order"
          :add-product-modal-id="addProductModalId"
          :load-order="loadOrder"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BDropdown,
  BDropdownItem,
  BButton,
  BModal,
  BCardText,
  BFormCheckbox,
} from 'bootstrap-vue'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import { apiToastWarning } from '@/@core/utils/toast'
import flatPickr from 'vue-flatpickr-component'
import analytics from '@/@core/utils/analytics'
import constants, { VUE_FLATPICKR_MONTH_CONFIG } from '@/constants'
import { formatObject } from '@/@core/utils/utils'
import { useOrderActions, isAcceptModificationsOrder } from '@/composables/useOrderActions'
import { useOrderPartialConfirm } from '@/composables/useOrderPartialConfirm'
import UserRoleMixinVue from '../UserRoleMixin.vue'
import AddOrderProducts from './AddOrderProducts.vue'

const { KP_DATE_FORMAT, KP_MONTH_FORMAT } = c
const { ORDER_PRODUCT_SHIPMENT_STATUS } = constants

const addProductModalId = 'add_products_modal'

export default {
  components: {
    BDropdown,
    BDropdownItem,
    BButton,
    BModal,
    BCardText,
    BFormCheckbox,
    FeatherIcon,
    flatPickr,
    AddOrderProducts,
  },
  mixins: [UserRoleMixinVue],
  props: {
    order: {
      type: Object,
      default() {
        return null
      },
    },
    totalUnits: {
      type: Number,
      default() {
        return 0
      },
    },
    modifyUsingImport: {
      type: Boolean,
      default: false,
    },
    loadOrder: {
      type: Function,
      default() {
        return Promise.resolve()
      },
    },
    isDirectBrand: {
      type: Boolean,
      default: false,
    },
    originalOrder: {
      type: Object,
      default() {
        return null
      },
    },
    isModifyEnabled: {
      type: Boolean,
      default: false,
    },
    isInDraft: {
      type: Boolean,
      default: false,
    },
    importedProductStatuses: {
      type: Array,
      default() {
        return []
      },
    },
    // KP-4363: product ids the brand ticked in the products list
    selectedProductIds: {
      type: Array,
      default() {
        return []
      },
    },
  },
  setup(props) {
    const actions = useOrderActions()
    const partialConfirm = useOrderPartialConfirm()
    actions.initOrderActions(props.order, props.isModifyEnabled)
    return {
      // State
      loading: actions.loading,
      isModify: actions.isModify,
      modalTitle: actions.modalTitle,
      modalBody: actions.modalBody,
      action: actions.action,
      hasError: actions.hasError,
      selectedAction: actions.selectedAction,
      expectedDate: actions.expectedDate,
      sendEmailToRetailer: actions.sendEmailToRetailer,
      approveActionText: actions.approveActionText,
      // Computeds
      isModifyOrderImportExportEnabled: actions.isModifyOrderImportExportEnabled,
      // Constants
      ORDER_STATUS: actions.ORDER_STATUS,
      SAVE: actions.SAVE,
      MODIFY: actions.MODIFY,
      MODIFY_AND_CONFIRM: actions.MODIFY_AND_CONFIRM,
      CONFIRM_SELECTED: actions.CONFIRM_SELECTED,
      KP_DATE_FORMAT,
      VUE_FLATPICKR_MONTH_CONFIG,
      KP_MONTH_FORMAT,
      addProductModalId,
      // Composable methods (prefixed)
      _isCancelEnabled: actions.isCancelEnabled,
      _isRejectEnabled: actions.isRejectEnabled,
      _isModifyButtonEnabled: actions.isModifyButtonEnabled,
      _isApproveEnabled: actions.isApproveEnabled,
      _isOrderActionEnabled: actions.isOrderActionEnabled,
      _openActionModal: actions.openActionModal,
      _approveOrderData: actions.approveOrderData,
      _acceptOrderModifications: actions.acceptOrderModifications,
      _rejectOrder: actions.rejectOrder,
      _cancelOrder: actions.cancelOrder,
      _cancelOrderOrModifications: actions.cancelOrderOrModifications,
      _saveOrder: actions.saveOrder,
      _enableModify: actions.enableModify,
      _onClickSaveDate: actions.onClickSaveDate,
      _getActionModalConfig: actions.getActionModalConfig,
      _getConfirmSelectedModalConfig: actions.getConfirmSelectedModalConfig,
      _syncPartialConfirmState: actions.syncPartialConfirmState,
      _getApproveActionText: actions.getApproveActionText,
      _isModifyProductsActionAllowed: actions.isModifyProductsActionAllowed,
      _isPartialConfirmEnabled: partialConfirm.isPartialConfirmEnabled,
      _confirmSelectedProducts: partialConfirm.confirmSelectedProducts,
    }
  },
  computed: {
    isCancelEnabled() {
      return this._isCancelEnabled(this.order)
    },
    isRejectEnabled() {
      return this._isRejectEnabled(this.order)
    },
    isModifyButtonEnabled() {
      return this._isModifyButtonEnabled(this.order)
    },
    isApproveEnabled() {
      return this._isApproveEnabled(this.order)
    },
    isOrderActionEnabled() {
      return this._isOrderActionEnabled(this.order)
    },
    canConfirmSelectedProducts() {
      return this._isPartialConfirmEnabled(this.order) && !this.isModify
    },
    isModifyProductsAllowed() {
      return this._isModifyProductsActionAllowed(this.order)
    },
  },
  watch: {
    modifyUsingImport(newValue) {
      if (newValue) {
        this.enableModify(newValue)
      }
    },
    order: {
      handler(orderData) {
        this.hasError = orderData.products.some(
          p => p.hasError && p.status !== ORDER_PRODUCT_SHIPMENT_STATUS.CANCELLED.LABEL,
        )
        this._syncPartialConfirmState(orderData)
        if (!this.isModify && orderData.status) {
          const actionMap = [
            { condition: this.isApproveEnabled, text: this._getApproveActionText(orderData), action: this.ORDER_STATUS.CONFIRMED },
            { condition: this.isCancelEnabled, text: 'Cancel', action: this.ORDER_STATUS.CANCELLED },
            { condition: this.isRejectEnabled, text: 'Reject', action: this.ORDER_STATUS.REJECTED },
          ]
          const matchedAction = actionMap.find(item => item.condition)
          if (matchedAction) {
            this.selectedAction = {
              text: matchedAction.text,
              action: matchedAction.action,
            }
          }
        }
      },
      deep: true,
    },
    sendEmailToRetailer(newValue) {
      if (this.action === this.SAVE) {
        analytics.track(
          constants.TRACKS.ACTIONS.SINGLE_ORDER.TOGGLE_SEND_NOTIFICATION,
          formatObject({
            orderId: this.order.orderSeqId,
            collectionId: this.order.collectionId,
            sendEmailToRetailer: newValue,
          }),
        )
      }
    },
  },
  emits: ['enable-modify-changes', 'update-order', 'revert-modified-changes', 'export-order-for-modify', 'open-import-modal', 'save-order-success'],
  mounted() {
    if (this.isModifyEnabled) {
      this.enableModify(true)
    } else {
      const actionMap = [
        { condition: this.isApproveEnabled, text: this._getApproveActionText(this.order), action: this.ORDER_STATUS.CONFIRMED },
        { condition: this.isCancelEnabled, text: 'Cancel', action: this.ORDER_STATUS.CANCELLED },
        { condition: this.isRejectEnabled, text: 'Reject', action: this.ORDER_STATUS.REJECTED },
      ]

      const matchedAction = actionMap.find(item => item.condition)
      if (matchedAction) {
        this.selectedAction = {
          text: matchedAction.text,
          action: matchedAction.action,
        }
      }
    }

    if (this.order?.expectedAvailabilityDate) {
      this.expectedDate = this.order.expectedAvailabilityDate
    }
  },
  methods: {
    toggleDatePicker() {
      this.$refs.expDate.$el._flatpickr.toggle()
    },
    setModalDataAndOpen({ title, body, action }) {
      this.modalTitle = title
      this.modalBody = body
      this.action = action
      this.$refs.actionModal.show()
    },
    openActionModal(actionType) {
      const config = this._openActionModal(actionType, {
        order: this.order,
        originalOrder: this.originalOrder,
        importedProductStatuses: this.importedProductStatuses,
        isInDraft: this.isInDraft,
      })
      if (config) {
        this.setModalDataAndOpen(config)
      }
    },
    validateExAvDate(err) {
      if (
        this.isBrand
        && err.response?.data?.data?.hasOwnProperty('isAvailableExAvDate')
        && !err.response.data.data.isAvailableExAvDate
      ) {
        this.$refs.actionModal.hide()
        this.$refs.expAvailability.show()
        setTimeout(() => {
          this.toggleDatePicker()
        }, 400)
      }
    },
    approveOrder() {
      if (isAcceptModificationsOrder(this.order)) {
        this.acceptOrderModifications()
        return
      }
      if (this.isBrand) {
        if (this.isModify) {
          apiToastWarning('You have some new changes. Please click modify before approve')
          return
        }
        if (!this.order.expectedAvailabilityDate && !this.isDirectBrand) {
          apiToastWarning('Please Enter Estimated Delivery Window')
          this.$refs.actionModal.hide()
          this.$refs.expAvailability.show()
          setTimeout(() => {
            this.toggleDatePicker()
          }, 400)
          return
        }
      }
      this.approveOrderData()
    },
    openConfirmSelectedModal() {
      if (!this.selectedProductIds.length) {
        apiToastWarning('Please select the products to confirm')
        return
      }
      this.setModalDataAndOpen(this._getConfirmSelectedModalConfig(this.selectedProductIds.length))
    },
    confirmSelectedProducts() {
      if (this.isModify) {
        apiToastWarning('You have some new changes. Please save them before confirming products')
        return
      }
      if (!this.order.expectedAvailabilityDate && !this.isDirectBrand) {
        apiToastWarning('Please Enter Estimated Delivery Window')
        this.$refs.actionModal.hide()
        this.$refs.expAvailability.show()
        setTimeout(() => {
          this.toggleDatePicker()
        }, 400)
        return
      }
      this.confirmSelectedProductsData()
    },
    confirmSelectedProductsData() {
      this.loading = true
      this._confirmSelectedProducts(this.order._id, this.selectedProductIds, this.loadOrder)
        .then(result => {
          this.loading = false
          if (result?.success) {
            this.$refs.actionModal.hide()
          } else if (result?.err) {
            this.validateExAvDate(result.err)
          }
        })
    },
    approveOrderData() {
      this._approveOrderData(this.$route.params.orderId, this.loadOrder)
        .then(result => {
          if (result?.success) {
            this.$refs.actionModal.hide()
          } else if (result?.err) {
            this.validateExAvDate(result.err)
          }
        })
    },
    acceptOrderModifications() {
      this._acceptOrderModifications(this.$route.params.orderId, this.loadOrder)
        .then(result => {
          if (result?.success) {
            this.$refs.actionModal.hide()
          }
        })
    },
    rejectOrder() {
      this._rejectOrder(this.$route.params.orderId, this.loadOrder)
    },
    cancelOrder() {
      this._cancelOrderOrModifications(this.order, this.$route.params.orderId, this.loadOrder)
    },
    saveOrder() {
      this._saveOrder({
        orderId: this.$route.params.orderId,
        order: this.order,
        originalOrder: this.originalOrder,
        importedProductStatuses: this.importedProductStatuses,
        loadOrder: this.loadOrder,
      }).then(result => {
        if (result?.success) {
          this.$refs.actionModal.hide()
          this.$emit('save-order-success')
        }
      })
    },
    trackAction(action) {
      // Status changes only: modify, save and confirm-selected are not tracked here
      const untrackedActions = [this.MODIFY, this.SAVE, this.CONFIRM_SELECTED]
      if (!untrackedActions.includes(action)) {
        analytics.track(
          this.isBrand
            ? constants.TRACKS.ACTIONS.BRAND_CHANGES_ORDER_STATUS
            : constants.TRACKS.ACTIONS.RETAILER_CHANGES_ORDER_STATUS,
          formatObject({
            orderId: this.order.orderSeqId,
            collectionName: this.order.collectionName,
            selectedStatus: action,
          }),
        )
      }
    },
    handleModifyAndConfirm() {
      if (!this.order?.expectedAvailabilityDate) {
        apiToastWarning('Please Enter Estimated Delivery Window')
        this.$refs.actionModal.hide()
        this.$refs.expAvailability.show()
        setTimeout(() => {
          this.toggleDatePicker()
        }, 400)
        return
      }
      this.$emit('update-order', true)
    },
    getActionHandler(action) {
      const actionHandlers = {
        [this.ORDER_STATUS.CONFIRMED]: () => this.approveOrder(),
        [this.ORDER_STATUS.CANCELLED]: () => this.cancelOrder(),
        [this.ORDER_STATUS.REJECTED]: () => this.rejectOrder(),
        [this.SAVE]: () => this.saveOrder(),
        [this.MODIFY]: () => this.$emit('update-order'),
        [this.MODIFY_AND_CONFIRM]: () => this.handleModifyAndConfirm(),
        [this.CONFIRM_SELECTED]: () => this.confirmSelectedProducts(),
      }
      return actionHandlers[action]
    },
    confirmAction(action) {
      const handler = this.getActionHandler(action)
      if (handler) {
        handler()
      }
      this.trackAction(action)
    },
    enableModify(enable = false) {
      this._enableModify(enable)
      if (this.isModify) {
        this.$emit('enable-modify-changes')
      } else {
        this.$emit('revert-modified-changes')
      }
    },
    async onClickSaveDate() {
      const result = await this._onClickSaveDate(this.order, this.$route.params.orderId)
      if (result?.success) {
        if (result.shouldApprove) {
          this.approveOrderData()
        } else if (result.shouldConfirmSelected) {
          this.$refs.expAvailability.hide()
          this.confirmSelectedProductsData()
        } else {
          this.$emit('update-order', true)
        }
      }
    },
  },
}
</script>

<style>
.modify-dropdown-toggle.dropdown-toggle-split {
  margin-left: 3px;
}
</style>
